import PublicSearchWritersStore from '@App/stores/search/public/public-search-writers-store'
import { appQueryClient } from '@App/configs/query-client'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { WriterSearch } from '@App/stores/search/models/writer-search'
import { v4 as uuidv4 } from 'uuid'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { SongwriterLink } from '@Entities/songwriter/songwriter-link'
import { makeObservable, observable, runInAction } from 'mobx'
import {
    linkSongwriterWriter,
    searchSongwriterWriterLinks,
    searchSongwriterWriters,
    unlinkSongwriterWriter,
    writerIpiUpdateRequest,
    writerMergeRequest,
    writerUpdateRequest,
} from '@App/api/routes/songwriter/songwriter'
import { PaginationBody } from '@Entities/pagination'
import RootStore from '@App/stores/root-store'
import { isEmptyObject } from '@App/helpers/check-is-empty-object'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { FilterFieldGroup } from '@App/forms/common/filter-field-group'
import { FormFieldTypes } from '@App/enum/form-field-types'
import { SongwriterWriterSearch } from '@App/stores/search/models/songwriter-writer-search'
import { generateSearchCriterionFromOption } from '@App/helpers/generate-search-criterion'
import { HasSearchFilter } from '@App/stores/search/helpers/has-search-filter'
import { SongwriterUpdateRequestFormData } from '@Scenes/SongwriterScene/SongwriterMyWritersScene/SongwriterRequestUpdateSidebar'

// Reason value the writer update request endpoint expects for a writer name merge.
const MERGE_WRITER_NAMES_REQUEST_REASON = 'MergeWriterNames'

export const SongwriterSearchFilters: Array<FilterFieldGroup<SongwriterWriterSearch>> = [
    {
        name: 'Writer filter',
        allowMultiple: false,
        filterFields: [
            {
                WRITER_FILTER: {
                    type: FormFieldTypes.RADIO,
                    name: 'inCatalogFilter',
                    options: [
                        { name: 'allWriters', label: 'Show all writers', default: true },
                        { name: 'onlyWritersInCatalog', label: 'Show only writers in my catalog' },
                        { name: 'excludeWritersInCatalog', label: 'Show only writers not in my catalog' },
                    ],
                    optional: false,
                    label: '',
                },
            },
        ],
    },
]

export const songwriterLinkSearchOptions: Array<SelectOptionHide<WriterSearch>> = [
    { id: uuidv4(), value: 'fullName', label: 'Writer Name', shouldHide: true },
    { id: uuidv4(), value: 'ipiNameNumber', label: 'Writer IPI', shouldHide: true },
]

export const defaultSongwriterLinkCriteria: Array<SearchCriterion<WriterSearch>> = [
    generateSearchCriterionFromOption(songwriterLinkSearchOptions[0], true, false, undefined),
]

export default class SongwriterWriterSearchStore
    extends PublicSearchWritersStore
    implements HasSearchFilter<SongwriterWriterSearch>
{
    readonly canAddCriteria: boolean = true
    readonly displayType: 'songwriter' | 'public' = 'songwriter'

    private _linkedSongwriters: SongwriterLink[] = []

    private _hasSearched = false

    public searchFilters = SongwriterSearchFilters

    constructor(rootStore: RootStore) {
        super(rootStore)
        makeObservable<SongwriterWriterSearchStore, '_linkedSongwriters' | '_hasSearched' | '_currentSearchFilters'>(
            this,
            {
                _linkedSongwriters: observable,
                _hasSearched: observable,
                _currentSearchFilters: observable,
            }
        )
    }

    private _currentSearchFilters: SongwriterWriterSearch = { inCatalogFilter: 'allWriters' }

    public setSearchFilters(filters: Partial<SongwriterWriterSearch>) {
        this._currentSearchFilters = filters
    }

    public setWriterNameForSearch(name: string) {
        this.writerSearch.fullName = name
    }

    private async searchForSongwritersCall(pagination: PaginationBody) {
        const writersSearchModel = await searchSongwriterWriters(
            pagination,
            this.rootStore.songwriterStore.currentSongwriterId,
            { ...this.writerSearch, ...this._currentSearchFilters }
        )
        return {
            data: writersSearchModel.data.content.map((writer) =>
                PublicSearchWritersStore.convertWriterToPartyDetails(writer)
            ),
            count: writersSearchModel.data.totalElements,
        }
    }

    async search(pagination: PaginationBody = { page: 1, limit: this._searchByCriteriaResultsPerPageLimit }) {
        if (this.writerSearch && (!isEmptyObject(this.writerSearch) || !isEmptyObject(this._currentSearchFilters))) {
            super.resetPartiesAndWorks()
            const result = await this.searchForSongwritersCall(pagination)
            runInAction(() => {
                this._searchResults = result.data
            })
            if (pagination.limit && pagination.page && result.data) {
                runInAction(() => {
                    this._totalNumberOfResults = result.count
                    this._searchByCriteriaPaginationInfo = calcNumPages(result.count, pagination.limit, pagination.page)
                })
            }
        }
        await this.loadSongwriterWriterLinks(this._searchResults.map((party) => Number(party.ipId)))
        runInAction(() => (this._hasSearched = true))
    }

    public loadSongwriterWriterLinks = async (writerIpIds: number[]) => {
        const songwriterLinks = await searchSongwriterWriterLinks(
            this.rootStore.songwriterStore.currentSongwriterId,
            writerIpIds
        )
        runInAction(() => {
            this._linkedSongwriters = songwriterLinks.data
        })
    }

    public toggleSongwriterLink = async (linkStatus: boolean, writerIpId: number) => {
        const songwriterId = this.rootStore.songwriterStore.currentSongwriterId

        if (linkStatus) {
            const songwriterLink = await linkSongwriterWriter(songwriterId, writerIpId)
            runInAction(() => this._linkedSongwriters.push(songwriterLink.data))
        } else {
            await unlinkSongwriterWriter(songwriterId, writerIpId)
            runInAction(() =>
                this._linkedSongwriters.splice(
                    this._linkedSongwriters.findIndex((writer) => writer.affWriterIpId === writerIpId),
                    1
                )
            )
        }

        await appQueryClient.invalidateQueries({
            queryKey: ['catalog-search', 'songwriter-catalog', songwriterId],
        })
    }

    public submitWriterUpdateRequest = async (
        writerIpId: number,
        writerName: string,
        reason: string,
        formData: SongwriterUpdateRequestFormData
    ) =>
        writerUpdateRequest(this.rootStore.songwriterStore.currentSongwriterId, writerIpId, {
            reason,
            writerName,
            correctSpelling: formData.correctSpelling,
            reasonForCorrection: formData.reasonForCorrection,
        })

    public submitWriterMergeRequest = async (
        primaryWriterIpId: number,
        primaryWriterName: string,
        mergeWriterIpIds: number[]
    ) =>
        writerMergeRequest(this.rootStore.songwriterStore.currentSongwriterId, primaryWriterIpId, {
            reason: MERGE_WRITER_NAMES_REQUEST_REASON,
            writerName: primaryWriterName,
            mergeWriterIpIds,
        })

    public submitWriterIpiUpdateRequest = async (writerIpId: number, newIpiNumber: string) =>
        writerIpiUpdateRequest(this.rootStore.songwriterStore.currentSongwriterId, writerIpId, newIpiNumber)

    createWriterSearchCriteriaStore(): SearchCriteriaStore<WriterSearch> {
        return new SearchCriteriaStore(
            defaultSongwriterLinkCriteria,
            songwriterLinkSearchOptions,
            SearchOptionType.filter,
            RequiredCriteria.some,
            2
        )
    }

    resetSearchCriteriaStore(): void {
        this._currentSearchCriteriaStore = this.createWriterSearchCriteriaStore()
    }

    resetStore(): void {
        super.resetStore()
        this._currentSearchFilters = { inCatalogFilter: 'allWriters' }
        this._hasSearched = false
    }

    get linkedSongwriters() {
        return this._linkedSongwriters
    }

    get hasSearched() {
        return this._hasSearched
    }
}
